import { CaretDown, X } from "@phosphor-icons/react"
import { t } from "i18next"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FormError } from "@/components/ui/form.tsx"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"

export type SelectUI = {
  value: string
  label: string
}

interface DropdownSelectProps {
  label: string
  options: { label: string; value: string }[]
  onSelect(value: string | undefined | null): void
  defaultValue: string | undefined | null
  testId: string
  isClearable?: boolean
  disabled?: boolean
  error?: string
  className?: string
  labelClassName?: string
  renderOption?: (
    option: SelectUI,
    index: number,
    update: {
      updateValue: (value: string | undefined | null) => void
      activeValue: string | undefined | null
    },
  ) => React.ReactNode
  ref?: React.Ref<HTMLButtonElement>
}

export const DropdownSelect = (props: DropdownSelectProps) => {
  const {
    label,
    onSelect,
    options,
    defaultValue: activeValue,
    testId,
    isClearable = false,
    disabled = false,
    error,
    className,
    renderOption,
    ref,
    labelClassName,
  } = props

  const updateValue = (value: string | undefined | null) => {
    onSelect?.(value)
  }

  const activeLabel = options.find((v) => v.value === activeValue)?.label
  return (
    <DropdownMenu defaultOpen={false}>
      <div className={className}>
        <DropdownMenuTrigger ref={ref} asChild data-testid={testId} disabled={disabled}>
          <button
            className={cn(
              "custom-dropdown-trigger bg-surface-subdued relative flex h-12 w-full items-center justify-between gap-2 rounded-md border p-1 pl-4 text-sm",
              error && "outline-critical! outline-1 outline-solid focus:outline-2",
            )}>
            <div>
              <label
                className={cn(
                  "absolute top-3.5 left-4 transition-all",
                  labelClassName,
                  activeValue ? "top-1 text-xs" : "top-3.5",
                )}>
                {label}
              </label>
              {activeValue && <span className="absolute top-5.25 w-full truncate pr-12 text-left">{activeLabel}</span>}
            </div>
            <div
              className={cn(
                "text-on-surface-subdued z-10 flex h-10 w-10 items-center justify-center rounded-3xl",
                !disabled && (isClearable || (!isClearable && !activeValue))
                  ? "hover:bg-surface-hovered focus-visible:bg-surface-focused active:bg-surface-pressed"
                  : "",
              )}
              onPointerDown={() => (activeValue && isClearable ? updateValue(undefined) : undefined)}>
              {isClearable && activeValue ? (
                <X size={24} className="text-on-surface-subdued" onClick={() => onSelect && onSelect("")} />
              ) : (
                <CaretDown
                  className={cn("h-6 w-6", disabled ? "text-on-surface-disabled" : "text-on-surface-subdued")}
                />
              )}
            </div>
          </button>
        </DropdownMenuTrigger>

        {error && <FormError>{t("common.fieldErrorList.requiredField")}</FormError>}
      </div>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className={cn(
          "h-(--radix-select-trigger-height)",
          "min-w-(--radix-dropdown-menu-trigger-width)",
          "max-w-(--radix-dropdown-menu-trigger-width)",
          "gap-1 rounded-xl p-3",
        )}>
        <ScrollArea>
          {options.map((option, idx) => {
            if (renderOption)
              return renderOption(option, idx, {
                updateValue,
                activeValue,
              })

            return (
              <DropdownMenuCheckboxItem
                className="data-[state='checked']:bg-primary-surface! data-disabled:text-on-surface-disabled data-[state='checked']:text-on-primary-surface! focus:bg-surface-hovered focus:text-on-surface mb-1 w-full transition-all duration-100 data-disabled:pointer-events-none"
                onSelect={() => updateValue(option.value)}
                key={option.value}
                checked={option.value === activeValue}
                testId={`${testId}-item`}>
                {option.label}
              </DropdownMenuCheckboxItem>
            )
          })}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
